import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

import { setStatesWhereAshIsAvailable } from 'store/signup/signupSlice';

import { logDataDogEvent } from 'utils/datadog';
import { session, SESSION_KEYS } from 'utils/storage';

import packageInfo from '../../../package.json';

import {
  CheckPricesPredictOnSignUpReqProps,
  CheckPricesPredictOnSignUpResProps,
  CreateAccountAfterPaymentReqProps,
  CreateAccountWithPaymentResProps,
  CreateProspectAccountReqProps,
  CreateProspectAccountResProps,
  CreateProspectUserReqProps,
  CreateProspectUserResProps,
  GetCampaignInfoResProps,
  GetLabsReqProps,
  GetLabsResProps,
  GetProviderImagesReqProps,
  GetProviderImagesResProps,
  GetStatesWhereAshIsAvailableReqProps,
  GetStatesWhereAshIsAvailableResProps,
  PaymentCombinedReqProps,
  PaymentCombinedResProps,
  SendOrchestrateFormReqProps,
  SendOrchestrateFormResProps
} from './signup.types';

export const signupApi = createApi({
  baseQuery: fetchBaseQuery({
    baseUrl: import.meta.env.VITE_BASE_URL,
    credentials: 'include',
    prepareHeaders: (headers, { endpoint }) => {
      const ENDPOINTS_TO_COLLECT_DATA = ['combinedPayment'];
      headers.set('API-KEY', import.meta.env.VITE_API_KEY ?? '');
      headers.set('App-Version', packageInfo.version);
      headers.set('Platform', 'web-patient');
      const onboardingSession = session.get(SESSION_KEYS.ONBOARDING_ID);
      if (onboardingSession && ENDPOINTS_TO_COLLECT_DATA.includes(endpoint)) {
        headers.set('onboarding-id', onboardingSession);
      }
      return headers;
    }
  }),
  endpoints: (build) => ({
    checkPricesPredictOnSignUp: build.mutation<
      CheckPricesPredictOnSignUpResProps,
      CheckPricesPredictOnSignUpReqProps
    >({
      query: (body) => ({
        body,
        method: 'POST',
        url: '/auth/payment-predict'
      })
    }),

    combinedPayment: build.mutation<PaymentCombinedResProps, PaymentCombinedReqProps>({
      query: ({ accessToken, ...body }) => ({
        body,
        headers: {
          Authorization: accessToken
        },
        method: 'POST',
        url: '/auth/subscription'
      })
    }),

    createProspectAccount: build.mutation<
      CreateProspectAccountResProps,
      CreateProspectAccountReqProps
    >({
      query: (body) => ({
        body,
        method: 'POST',
        url: '/auth/patients/prospect'
      })
    }),
    createProspectUser: build.mutation<CreateProspectUserResProps, CreateProspectUserReqProps>({
      query: (body) => ({
        body,
        method: 'POST',
        url: '/auth/create-prospect'
      })
    }),
    createUserDetailsAfterPayment: build.mutation<
      CreateAccountWithPaymentResProps,
      CreateAccountAfterPaymentReqProps
    >({
      query: ({ accessToken, sessionId, ...body }) => ({
        body: {
          ...body,
          flowUrl: window.location.href,
          referrerUrl: window.document.referrer
        },
        headers: {
          Authorization: accessToken,
          ...(sessionId && { 'session-id': sessionId })
        },
        method: 'POST',
        url: '/auth/create-account/personal-info-new-flow'
      })
    }),
    getCampaignInfo: build.query<GetCampaignInfoResProps, string>({
      query: (id) => `/auth/campaigns?recId=${id}`
    }),
    getLabs: build.query<GetLabsResProps, GetLabsReqProps>({
      query: ({ labType, zipCode }) => ({
        params: {
          labType,
          zipCode
        },
        url: '/auth/labs'
      })
    }),
    getOrchestrateSessionInfo: build.query<SendOrchestrateFormResProps, string>({
      query: (accessToken) => ({
        credentials: 'include',
        headers: {
          ...(accessToken && { Authorization: accessToken })
        },
        url: '/auth/orchestrate-signup'
      })
    }),
    getProviderImages: build.query<GetProviderImagesResProps, GetProviderImagesReqProps>({
      query: ({ accessToken }) => ({
        headers: {
          ...(accessToken && { Authorization: accessToken })
        },
        url: '/auth/create-patient-account/default-profile-images'
      })
    }),
    getStatesWhereAshIsAvailable: build.query<
      GetStatesWhereAshIsAvailableResProps,
      GetStatesWhereAshIsAvailableReqProps
    >({
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          if (data?.data) {
            dispatch(setStatesWhereAshIsAvailable(data.data.states));
          }
        } catch (e) {
          logDataDogEvent({
            context: { error: e },
            level: 'error',
            message: 'getStatesWhereAshIsAvailable failed'
          });
        }
      },
      query: (params) => ({ params, url: '/auth/ash-lab-states' })
    }),
    sendOrchestrateForm: build.mutation<SendOrchestrateFormResProps, SendOrchestrateFormReqProps>({
      query: ({ accessToken, existingUser, idempotencyKey, ...body }) => ({
        body: { ...body, ...(existingUser && { existingUser }) },
        credentials: 'include',
        headers: {
          ...(accessToken && { Authorization: accessToken }),
          // Only sent for the terminal submit; the backend guard is a no-op without it.
          ...(idempotencyKey && { 'Idempotency-Key': idempotencyKey })
        },
        method: 'POST',
        url: '/auth/orchestrate-signup'
      })
    })
  }),
  reducerPath: 'signupApi'
});

export const {
  useCheckPricesPredictOnSignUpMutation,
  useCombinedPaymentMutation,
  useCreateProspectAccountMutation,
  useCreateProspectUserMutation,
  useGetStatesWhereAshIsAvailableQuery,
  useLazyGetCampaignInfoQuery,
  useLazyGetLabsQuery,
  useLazyGetOrchestrateSessionInfoQuery,
  useLazyGetStatesWhereAshIsAvailableQuery,
  useSendOrchestrateFormMutation
} = signupApi;
